import { FormLayout, Remark, useDownload } from "@careinfo/layout";
import { AccessControl } from "@careinfo/permission-required";
import { tw } from "@careinfo/tailwind";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import moment from "moment";
import { ChangeEvent, ClipboardEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import APIS from "../../constant/EndPoint";
import useGetHook from "../../customHooks/useGetHook";
import usePatchHook from "../../customHooks/usePatchHook";
import DownloadIcon from "../../Icons/DownloadIcon";
import { createRunsheetValidationForm } from "../../utils/validationSchemas";
import Card from "../Reusable/Card";
import ComboboxInput from "../Reusable/ComboboxInput";
import DataTable from "../Reusable/DataTable";
import Modal from "../Reusable/Modal";
import Spinner from "../Reusable/Spinner";
import TextField from "../Reusable/TextInput";
import { getManifestItemColumn } from "../TableColumns/ManifestTableColumns";
import { getRemainningOrderColumn } from "../TableColumns/RemainingOrderTable";
import ChangeStatus from "./ChangeStatus";

const EditManifest = (props: any) => {
  const {
    handleEdit,
    isLoading,
    data,
    gridData,
    id,
    handleAttachConsignment,
    handlePrint,
    isPrintLoading,
    handleSplitPrint,
    handleBranchPrint,
    handleStatusChange,
    defaultValue,
    isRemoved,
    handleRemove,
    refetch,
  } = props;
  const { download } = useDownload();

  const [searchParams, setSearchParams] = useSearchParams({
    limit: "10",
    page: "0",
    searchQuery: "",
  });
  const { mutateAsync: setMarkAsDone, isLoading: isMarkAsDoneLoading } =
    usePatchHook({
      queryKey: [`manifest-${id}`, "manifest"],
      navigateURL: "",
    });

  const [orderId, _setOrderId] = useState(null);
  const [openStatusPopup, setOpenStatusPopup] = useState(false);
  const [statusList, setStatusList] = useState(null);
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState("");
  const [freightValue, setFreightValue] = useState(null);
  const [advanceValue, setAdvanceValue] = useState(null);
  const [balance, setBalance] = useState(null);
  const [showLogistic, setShowLogistion] = useState(false);
  const [showMarkAsDoneModal, setShowMarkAsDoneModal] = useState(false);
  const selectedOrders = searchParams.get("selectedOrders");
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    getValues,
    clearErrors,
  } = useForm<any>({
    resolver: yupResolver(createRunsheetValidationForm),
    defaultValues: async () => {
      return {
        driver: {
          name: data?.driver?.user?.full_name,
          id: data?.driver?.id,
        },
        vehicle: {
          name: data?.vehicle?.license_number,
          id: data?.vehicle?.id,
        },
        destination_branch: {
          name: data?.destination_branch?.name,
          id: data?.destination_branch?.id,
        },
        freight: data?.freight,
        advance: data?.advance,
        balance: data?.balance,
        note: data?.note,
      };
    },
  });

  const { data: remainingConsignment, isLoading: isGettingRemaingConsignment } =
    useGetHook({
      url: APIS.LOGISTIC_ORDER,
      queryKey: ["remaing-consignment"],
      params: {
        manifest_id__isnull: true,
        to_branch: data?.destination_branch?.id,
        limit: 1,
        offset: 0,
      },
    });

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter" && event.target.value?.length > 0) {
      if (!data?.is_active) {
        toast.error("Order can't be received here");
      } else if (statusData?.length > 1 && defaultValue?.length === 1) {
        handleAttachConsignment(
          event.target.value,
          defaultValue[0]?.id,
          getValues("transit_branch"),
          setError
        );
      } else if (statusData?.length === 1) {
        handleAttachConsignment(
          event.target.value,
          statusData[0]?.id,
          getValues("transit_branch"),
          setError
        );
      } else {
        toast.error("Please select one status first");
      }
      event.target.value = "";
    }
  };
  const checkedAllItems = searchParams.get("selectAllItems") === "1";

  const handleIndividualSelect = (value: string, order: string) => {
    let selectedItems = searchParams.get("selectedItems")?.split(",");
    let selectedOrders = searchParams.get("selectedOrders")?.split(",");
    if (
      !selectedItems ||
      !selectedItems === undefined ||
      !selectedOrders ||
      !selectedItems === undefined
    ) {
      searchParams.set("selectedItems", value);
      searchParams.set("selectedOrders", order);
      setSearchParams(searchParams);
      return;
    }
    let itemIndex = selectedItems?.findIndex((item) => item === value);
    let orderIndex = selectedOrders?.findIndex((item) => item === order);
    if (itemIndex > -1 || orderIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      selectedOrders.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      const newSelectedOrders = selectedOrders
        ?.map((item) => item)
        .join(",")
        .toString();
      if (newSelectedItems.length || newSelectedOrders.length) {
        searchParams.set("selectedItems", newSelectedItems);
        searchParams.set("selectedOrders", newSelectedOrders);
      } else {
        searchParams.delete("selectedItems");
        searchParams.delete("selectedOrders");
      }
      searchParams.set("selectAllItems", "0");
      setSearchParams(searchParams);
    } else {
      selectedItems?.push(value);
      selectedOrders?.push(order);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      const newSelectedOrders = selectedOrders
        ?.map((item) => item)
        .join(",")
        .toString();
      searchParams.set("selectedItems", newSelectedItems);
      searchParams.set("selectedOrders", newSelectedOrders);
      setSearchParams(searchParams);
    }
  };

  const isSelected = (value: string, order: string) => {
    const selectedItems = searchParams.get("selectedItems")?.split(",");
    const selectedOrders = searchParams.get("selectedOrders")?.split(",");
    if (!selectedItems && !selectedOrders) {
      return false;
    }
    return selectedItems.includes(value), selectedOrders.includes(order);
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const isSelected = searchParams.get("selectAllItems");
    if (isSelected === "1") {
      searchParams.set("selectAllItems", "0");
      searchParams.delete("selectedItems");
      searchParams.delete("selectedOrders");
      setSearchParams(searchParams);
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(",")
        .toString();
      const selectedOrders = tableData
        ?.map((item: any) => item?.order?.id ?? item.id)
        .join(",")
        .toString();
      searchParams.set("selectAllItems", "1");
      searchParams.set("selectedItems", selectedItems);
      searchParams.set("selectedOrders", selectedOrders);
      setSearchParams(searchParams);
    }
  };
  const manifestItemColumn = getManifestItemColumn({
    checkedAllItems,
    handleSelectAll,
    isSelected,
    handleIndividualSelect,
    setSelectedOrder,
    setOpenReviewModal,
  });

  const remaingOrderColumn = getRemainningOrderColumn({
    checkedAllItems,
    handleSelectAll,
    isSelected,
    handleIndividualSelect,
  });

  const preventCopyPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    toast.error("Copy and paste is not allowed here");
  };

  const handleOrderDetailNavigate = () => {};

  const handleDownloadExcel = async () => {
    try {
      await download(
        `logisticservice/api/order/download-excel/`,
        `manifest-order-${moment().format("MMMM-Do-YYYY-h-mm-ss")}.xlsx`,
        selectedOrders
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleFreightvalue = (event: ChangeEvent<HTMLInputElement>) => {
    setFreightValue(event.target.value);
  };
  const handleAdvancevalue = (event: ChangeEvent<HTMLInputElement>) => {
    setAdvanceValue(event.target.value);
  };

  const handleMarkAsDone = () => {
    setShowMarkAsDoneModal(true);
  };

  const handleMarkAsDoneModalClose = () => {
    setShowMarkAsDoneModal(false);
  };

  const toggleMarkAsDone = async () => {
    const url = `${APIS.MANIFEST}${id}/toggle-is-active/`;
    let formData = {};
    try {
      await setMarkAsDone({ url, formData, setError });
      await refetch();
    } catch (err) {
      console.error(err);
    } finally {
      handleMarkAsDoneModalClose();
    }
  };

  useEffect(() => {
    let value = freightValue - advanceValue;
    setBalance(value);
  }, [freightValue, advanceValue]);
  useEffect(() => {
    if (gridData) {
      setStatusList(gridData?.results);
    }
  }, [gridData]);
  useEffect(() => {
    statusList?.map((x: any) => {
      if (x?.name === "Intermediate") {
        setStatusData(x?.status_id);
      }
    });
  }, [statusList]);

  return (
    <>
      <AccessControl
        children={
          <FormLayout
            handleSubmit={handleSubmit}
            handleFormSubmit={handleEdit}
            setError={setError}
            buttonLabel={"Update"}
            isLoading={isLoading}
            disabled={!data?.is_active}
          >
            <div className={tw`grid  md:grid-cols-3 gap-x-4 mx-4`}>
              <ComboboxInput
                url={APIS.VEHICLE}
                queryKey={"vehicle"}
                register={register("vehicle")}
                errors={errors?.vehicle}
                name={"vehicle"}
                label={"Vehicle Number"}
                props={{
                  is_active: true,
                }}
                setValue={setValue}
                placeholder={"Search vehicle"}
                getValues={getValues}
                clearErrors={clearErrors}
                disabled={!data?.is_active}
              />
              <ComboboxInput
                url={APIS.DRIVER}
                queryKey={"driver"}
                register={register("driver")}
                errors={errors?.driver}
                name={"driver"}
                label={"Driver"}
                setValue={setValue}
                props={{
                  is_active: true,
                }}
                placeholder={"Search driver"}
                getValues={getValues}
                clearErrors={clearErrors}
                disabled={!data?.is_active}
              />
              <ComboboxInput
                url={APIS.BRANCH}
                queryKey={"branch"}
                register={register("destination_branch")}
                errors={errors?.destination_branch}
                name={"destination_branch"}
                label={"Branch"}
                setValue={setValue}
                props={{
                  is_active: true,
                }}
                placeholder={"Search branch"}
                getValues={getValues}
                clearErrors={clearErrors}
                disabled={!data?.is_active}
              />
              <div className={tw`w-full my-2 text-left mt-4`}>
                <label htmlFor="">
                  Freight
                  <input
                    name={"freight"}
                    type={"number"}
                    value={freightValue}
                    {...register("freight")}
                    disabled={!data?.is_active}
                    onChange={handleFreightvalue}
                    className={tw`w-full focus:outline-none bg-[#EEF5F4] p-2 mt-1`}
                  />
                </label>
                {errors && (
                  <p className={tw`text-red-500 text-xs my-1`}>
                    {errors?.frieght?.message}
                  </p>
                )}
              </div>
              <div className={tw`w-full my-2 text-left mt-4`}>
                <label htmlFor="">
                  Advance
                  <input
                    name={"advance"}
                    type={"number"}
                    value={advanceValue}
                    disabled={!data?.is_active}
                    {...register("advance")}
                    onChange={handleAdvancevalue}
                    className={tw`w-full focus:outline-none bg-[#EEF5F4] p-2 mt-1`}
                  />
                </label>
                {errors && (
                  <p className={tw`text-red-500 text-xs my-1`}>
                    {errors?.advance?.message}
                  </p>
                )}
              </div>

              <div className={tw`w-full my-2 text-left mt-4`}>
                <label htmlFor="">
                  Balance
                  <input
                    name={"balance"}
                    type={"number"}
                    value={balance}
                    {...register("balance")}
                    disabled={true}
                    className={tw`w-full focus:outline-none bg-[#EEF5F4] p-2 mt-1`}
                  />
                </label>
                {errors && (
                  <p className={tw`text-red-500 text-xs my-1`}>
                    {errors?.balance?.message}
                  </p>
                )}
              </div>
            </div>
            <div className={tw`mx-4`}>
              <TextField
                label={"Note"}
                register={register(`note`, { disabled: !data?.is_active })}
                errors={errors?.note}
              />
            </div>
          </FormLayout>
        }
        allowedPermissions={["can_editupdate_manifest"]}
      />
      <Card title="Barcode Scanner">
        <>
          <div className={tw`flex flex-col  items-end w-full`}>
            <div
              className={tw`flex flex-row gap-x-4 items-center w-full justify-end`}
            >
              {/* <div className={tw`w-64 relative -top-2`}>
                {data?.is_active ? (
                  <ComboboxInput
                    url={APIS.BRANCH}
                    queryKey={"transit-branch"}
                    register={register("transit_branch")}
                    errors={errors?.transit_branch}
                    name={"transit_branch"}
                    label={"Transit Branch"}
                    setValue={setValue}
                    placeholder={"Search transit branch"}
                    getValues={getValues}
                    clearErrors={clearErrors}
                  />
                ) : (
                  <ComboboxInput
                    url={`${APIS.MANIFEST}${id}/transit-branch/`}
                    queryKey={"avaliable-branch"}
                    register={register("transit_branch")}
                    errors={errors?.transit_branch}
                    name={"transit_branch"}
                    label={"Transit Branch"}
                    setValue={setValue}
                    placeholder={"Search transit branch"}
                    getValues={getValues}
                    clearErrors={clearErrors}
                  />
                )}
              </div> */}
              <ChangeStatus
                gridData={statusData}
                defaultValue={defaultValue}
                handleStatusChange={handleStatusChange}
              />
            </div>

            <div className={tw`grid grid-cols-1 w-full`}>
              <div>
                <input
                  type="text"
                  {...register("tracking_number")}
                  className={tw`w-full focus:outline-none bg-[#EEF5F4] p-2 mt-1`}
                  onPaste={preventCopyPaste}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>
          </div>
        </>
      </Card>
      <>
        {!isGettingRemaingConsignment && (
          <button
            className={tw`p-2 flex gap-x-2 bg-primary-light my-2 rounded text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
            onClick={() => setShowLogistion((prev) => !prev)}
          >
            Remaining Consignment: {remainingConsignment.count}
          </button>
        )}
      </>

      {!showLogistic ? (
        <DataTable
          url={`${APIS.MANIFEST_ITEMS}`}
          queryKey={"manifest-items"}
          columns={manifestItemColumn}
          params={{ manifest: data?.id, order_by: "-modified" }}
          tableName={"order-table"}
          handleDetailNavigate={handleOrderDetailNavigate}
          actionMenu={
            <div className={tw`w-full text-right flex  items-center`}>
              <button
                className={tw`flex gap-x-1 cursor-pointer mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                onClick={handleDownloadExcel}
              >
                <DownloadIcon color={"var(--primary)"} />
                Download
              </button>

              {data?.is_active ? (
                <button
                  className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
                  onClick={handleMarkAsDone}
                >
                  Mark As Done
                </button>
              ) : null}
              <button
                className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
                disabled={isPrintLoading}
                onClick={() => {
                  data?.is_active
                    ? toast.error("Please Mark as done first")
                    : handleSplitPrint();
                }}
              >
                {isPrintLoading ? <Spinner /> : "Split Print"}
              </button>
              <button
                className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
                disabled={isPrintLoading}
                onClick={() => {
                  data?.is_active
                    ? toast.error("Please Mark as done first")
                    : handleBranchPrint();
                }}
              >
                {isPrintLoading ? <Spinner /> : "Branch Wise Print"}
              </button>
              <button
                className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                disabled={isPrintLoading}
                onClick={() => {
                  data?.is_active
                    ? toast.error("Please Mark as done first")
                    : handlePrint(getValues("transit_branch"));
                }}
              >
                {isPrintLoading ? <Spinner /> : "Print"}
              </button>
              {data?.is_active ? (
                <button
                  className={tw`p-1 px-5  rounded-sm border border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                  disabled={isRemoved}
                  onClick={() => handleRemove()}
                >
                  {isRemoved ? <Spinner /> : "Remove"}
                </button>
              ) : (
                ""
              )}
            </div>
          }
        />
      ) : (
        <DataTable
          url={`${APIS.LOGISTIC_ORDER}`}
          queryKey={"remaining-order"}
          columns={remaingOrderColumn}
          params={{
            manifest_id__isnull: true,
            to_branch: data?.destination_branch?.id,
          }}
          tableName={"remaining-consignement"}
          handleDetailNavigate={handleOrderDetailNavigate}
        />
      )}
      <Modal
        isOpen={showMarkAsDoneModal}
        setIsOpen={setShowMarkAsDoneModal}
        showTitle={false}
      >
        <div className={tw`grid place-items-center w-fit`}>
          <div className={tw`space-y-3`}>
            <h2 className={tw`text-xl font-bold text-center`}>Are you sure?</h2>
            <div className={tw`flex items-center gap-5 justify-end`}>
              <button
                className={tw`bg-gray-200 rounded-lg px-2 py-2 focus:outline-none`}
                onClick={handleMarkAsDoneModalClose}
              >
                Cancel
              </button>
              <button
                onClick={toggleMarkAsDone}
                className={tw`px-2 py-2 rounded-lg bg-primary  text-white disabled:bg-primary-light`}
                disabled={isMarkAsDoneLoading}
              >
                <p>Mark As done</p>
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={openReviewModal}
        setIsOpen={setOpenReviewModal}
        title={"Remark"}
      >
        <Remark selectedOrder={selectedOrder} />
      </Modal>
      <Modal
        isOpen={openStatusPopup}
        setIsOpen={setOpenStatusPopup}
        title={"Change Status To"}
      >
        <ChangeStatus
          handleAttachConsignment={handleAttachConsignment}
          openStatusPopup={openStatusPopup}
          setOpenStatusPopup={setOpenStatusPopup}
          orderId={orderId}
        />
      </Modal>
    </>
  );
};

export default EditManifest;
